import Button from '@/decodo/components/atoms/Button';
import Card from '@/decodo/components/atoms/Card';
import Heading from '@/decodo/components/atoms/Heading';
import { NextImageProps } from '@/decodo/components/atoms/Image';
import { Text } from '@/decodo/components/atoms/Text';
import { cn } from '@/lib/utils';
import { gradientDecodoBorderStyling } from '@/styles/gradient';

import { useEffect, useRef, useState } from 'react';
import { Swiper } from 'swiper/types';

export type CarouselCardProps = {
  swiper?: Swiper;
  slides: {
    title: string;
    description?: React.ReactNode;
    badge: React.ReactNode;
    image: NextImageProps;
  }[];
  activeSlideIndex: number;
};

export function CarouselCard<T>({
  swiper,
  slides,
  activeSlideIndex,
}: CarouselCardProps) {
  const slide = slides[activeSlideIndex];

  const carouselSwitchIntervalMs = 7000;
  const loaderRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<'0%' | '100%'>('0%');

  useEffect(() => {
    const loader = loaderRef.current;
    if (!loader) return;

    loader.style.transition = `width 0ms linear`;
    setWidth('0%');

    const timeout = setTimeout(() => {
      if (!loader) return;

      loader.style.transition = `width ${carouselSwitchIntervalMs}ms linear`;
      setWidth('100%');
    }, 50);

    return () => {
      loader.style.transition = `width 0ms linear`;
      setWidth('0%');
      clearTimeout(timeout); // Cleanup
    };
  }, [activeSlideIndex]);

  return (
    <Card
      color='neutral'
      roudnessLevel='s'
      padSize='l'
      classNames={{
        root: cn(
          'dark:bg-gray-50 dark:text-base-white',
          'bg-base-white text-gray-950',
          'gap-y-4 flex flex-col',
          gradientDecodoBorderStyling({
            borderWidth: 'p-px',
            color: 'gradient-main',
            borderRadius: 'rounded',
          })
        ),
      }}
    >
      <div className='flex flex-col gap-y-3'>
        <div className='overflow-hidden'>
          <div key={activeSlideIndex}>{slide.badge}</div>
        </div>
        <div className='overflow-hidden'>
          <div key={activeSlideIndex}>
            <Heading level='h3'>{slide.title}</Heading>
          </div>
        </div>
      </div>
      <div className='overflow-hidden'>
        <div key={activeSlideIndex}>
          <Text>{slide.description}</Text>
        </div>
      </div>
      <div className='mt-5 flex items-center gap-x-6'>
        <div className='flex gap-x-3'>
          {slides.map((_, i) => (
            <Button
              key={i}
              noStyles
              classNames={{
                root: cn(
                  'h-2 w-2  rounded-full duration-150',
                  'bg-base-black',
                  i !== activeSlideIndex && 'bg-gray-200 hover:bg-gray-500'
                ),
              }}
              onClick={() => swiper?.slideTo(i)}
            >
              <div className='sr-only'>Go to slide {i + 1}</div>
            </Button>
          ))}
        </div>
        <div className='flex h-px flex-grow overflow-hidden bg-gray-200'>
          {
            <div
              ref={loaderRef}
              className='bg-base-black h-full origin-left'
              style={{ width }}
            />
          }
        </div>
      </div>
    </Card>
  );
}
